// Module 1 — Welcome to Budgeting Basics
// ./index.ts owns this module's key and display name; this file owns its
// action items.

// Libs
import { useParams } from 'react-router';

// RDS
import BabyStepsGem from '@ramsey-design-system/baby-steps-gem';
import Heading from '@ramsey-design-system/heading';
import { VideoOutlinedIcon } from '@ramsey-design-system/icons';
import Link from '@ramsey-design-system/link';
import Text from '@ramsey-design-system/text';

// Components
import Divider from '~fpu/shared/components/Divider';
import DiscussionQuestions, {
  DiscussionQuestion
} from '../DiscussionQuestions';
import HLSVideoPlayer from '~fpu/shared/components/HLSVideoPlayer';
import NoResponsesMessage from '~fpu/modules/FpuDashboard/components/LessonChecklist/NoResponseMessage';
import QrCode from '~fpu/shared/components/QrCode';
import QrCodePrompt from '../QrCodePrompt';
import QuizResults from '~fpu/modules/FpuDashboard/components/LessonChecklist/QuizResults';

// Hooks
import { useWorkshopQuizResults } from '~fpu/modules/FpuDashboard/components/LessonChecklist/hooks/useQuizResults';

// Utils
import { PATHS, ZENDESK_BRAND_PARAM } from '~fpu/config/constants';
import storeConfig from '~fpu/config/storeConfig';

// Types
import { BabyStep } from '~fpu/types';
import { WorkshopActionItemDefinition } from '../../types';

function GettingStarted() {
  return (
    <div className="flex flex-col items-center gap-4 text-center">
      <img
        src="https://cdn.ramseysolutions.net/media/ramsey-plus/financial-peace/coordinator/dashboard/budget-workshop-assets/bible-icon.png"
        alt=""
        className="h-16 w-16"
      />
      <blockquote className="border-blue-40 border-l-md max-w-prose pl-3 text-left">
        <Text className="text-7 text-blue-90 leading-lg italic">
          &ldquo;Suppose one of you wants to build a tower. Won&rsquo;t you
          first sit down and estimate the cost to see if you have enough money
          to complete it?&rdquo;
        </Text>
        <Text className="text-4 mt-2 font-semibold text-gray-50 not-italic">
          Luke 14:28 (NIV)
        </Text>
      </blockquote>
    </div>
  );
}

function StepTwoInstructions() {
  return (
    <section>
      <Heading level="3" size="medium" className="mb-2">
        Step 2
      </Heading>
      <div className="text-4 mb-4">
        <Text className="font-semibold">If You Already Have a Password:</Text>
        <ul className="ml-2 list-outside">
          <li className="list-disc">
            Enter your email you used to register for the class
          </li>
          <li className="list-disc">
            Type your password and click{' '}
            <span className="font-semibold italic">Log In</span>
          </li>
        </ul>
      </div>

      <div className="text-4 mb-4">
        <Text className="font-semibold">If You Forgot Your Password:</Text>
        <ul className="ml-2 list-outside">
          <li className="list-disc">Enter your email</li>
          <li className="list-disc">
            Click <span className="font-semibold italic">Continue</span>
          </li>
          <li className="list-disc">
            Click{' '}
            <span className="font-semibold italic">Need Help Signing in?</span>
          </li>
          <li className="list-disc">
            Follow instructions for setting new password
          </li>
        </ul>
      </div>

      <div className="text-4 mb-3">
        <Text className="font-semibold">If You Never Set Up a Password:</Text>
        <ul className="ml-2 list-outside">
          <li className="list-disc">Enter your email</li>
          <li className="list-disc">
            Click <span className="font-semibold italic">Continue</span>
          </li>
          <li className="list-disc">
            Click{' '}
            <span className="font-semibold italic">Need Help Signing in?</span>
          </li>
          <li className="list-disc">
            Follow instructions for setting new password
          </li>
        </ul>
      </div>
      <Text className="text-4">
        Still need help logging in?{' '}
        <Link
          href={`https://financialpeace.help.ramseysolutions.com/hc/en-us/sections/360008371072-Account-Billing${ZENDESK_BRAND_PARAM}`}
          target="_blank"
          className="text-blue-40! no-underline! hover:underline!"
        >
          Click here.
        </Link>
      </Text>
    </section>
  );
}

function SettingUpYourAccount() {
  return (
    <div className="flex flex-col pt-2.5 pb-3 sm:flex-row sm:pb-0">
      <section className="flex flex-col justify-between">
        <article>
          <Heading level="3" size="medium" className="mb-1">
            Step 1
          </Heading>
          <Text size="bodyLarge" className="mb-2.5">
            Scan the QR Code
          </Text>
          <QrCode
            size={108}
            url="https://www.fpu.com/member"
            elementId="Check-In"
            hasIcon={false}
            fileName="fpu-member-dashboard-qr-code"
            data-testid="qr-code-icon"
            className="gap-sm! items-start!"
          />
          <Text size="bodyLarge" className="mb-4 sm:mb-5">
            Or go to <br /> fpu.com/member
          </Text>
        </article>
      </section>
      <div className="border-sm border-gray-20 bg-gray-20 mr-3 ml-2 box-border w-px rounded-full" />
      <StepTwoInstructions />
    </div>
  );
}

const GUIDES: { name: string; bio: string; image: string }[] = [
  {
    name: 'Dave Ramsey',
    bio: 'Founder of Ramsey Solutions and creator of Financial Peace University, whose no-nonsense approach to money has helped millions get out of debt and build wealth.',
    image: 'https://cdn.ramseysolutions.net/media/fpu/fpu_member/dave-pic.png'
  },
  {
    name: 'Rachel Cruze',
    bio: 'Bestselling author and personal finance expert who helps people take control of their money with practical, real-life advice.',
    image: 'https://cdn.ramseysolutions.net/media/fpu/fpu_member/rachel-pic.png'
  },
  {
    name: 'George Kamel',
    bio: 'Personal finance expert and Ramsey personality known for breaking down complex money topics in a straightforward, relatable way.',
    image: 'https://cdn.ramseysolutions.net/media/fpu/fpu_member/george-pic.png'
  },
  {
    name: 'Jade Warshaw',
    bio: 'Debt-free coach and Ramsey personality who paid off nearly $460,000 in debt alongside her husband and now helps others do the same.',
    image: 'https://cdn.ramseysolutions.net/media/fpu/fpu_member/jade-pic.png'
  }
];

function MeetYourGuides() {
  return (
    <div className="flex flex-col gap-3 pt-1.5">
      <Text className="text-4 mb-1">
        Get to know the Ramsey personalities guiding you through each session.
      </Text>
      <ul className="flex flex-col gap-4">
        {GUIDES.map(({ name, bio, image }) => (
          <li key={name} className="flex items-start gap-3">
            <img
              src={image}
              alt={name}
              className="h-16 w-16 shrink-0 rounded-full object-cover"
            />
            <div>
              <Text className="text-4 font-bold">{name}</Text>
              <Text className="text-4 leading-lg">{bio}</Text>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

const BABY_STEPS_LIST: { step: BabyStep; description: string }[] = [
  { step: '1', description: 'Save $1,000 for your starter emergency fund.' },
  {
    step: '2',
    description: 'Pay off all debt (except the house) using the debt snowball.'
  },
  {
    step: '3',
    description: 'Save 3-6 months of expenses in a fully funded emergency fund.'
  },
  {
    step: '4',
    description: 'Invest 15% of your household income in retirement.'
  },
  { step: '5', description: "Save for your children's college fund." },
  { step: '6', description: 'Pay off your home early.' },
  { step: '7', description: 'Build wealth and give.' }
];

function TheSevenBabySteps() {
  return (
    <>
      <Text className="text-4 mb-3">
        Find the Baby Step that best describes where you are today. This helps
        you focus on what matters most for your situation right now.
      </Text>
      <ul className="flex flex-col gap-3 pt-1.5">
        {BABY_STEPS_LIST.map(({ step, description }) => (
          <li key={step} className="flex items-start gap-3">
            <BabyStepsGem appearance="primary" size="md" step={step} />
            <div>
              <Text className="text-4 font-bold">Baby Step {step}</Text>
              <Text className="text-4 leading-lg">{description}</Text>
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}

function ZeroBasedBudgetClip() {
  return (
    <HLSVideoPlayer
      src="https://cdn.ramseysolutions.net/media/ramsey-plus/financial-peace/coordinator/dashboard/budget-workshop-assets/zero-based-budget.mp4"
      thumbnail="https://cdn.ramseysolutions.net/media/ramsey-plus/financial-peace/coordinator/dashboard/budget-workshop-assets/zero-based-budget.png"
    />
  );
}

function NerdFreeSpiritQuiz() {
  const NERD_FREE_SPIRIT_SPENDER_SAVER_QUIZ =
    'nerd-free-spirit-spender-saver-quiz';

  const { workshopId } = useParams() as { workshopId: string };
  const { data } = useWorkshopQuizResults(
    workshopId,
    NERD_FREE_SPIRIT_SPENDER_SAVER_QUIZ
  );

  const hasResponses =
    data?.some((question) =>
      question.options?.some((option) => option.count > 0)
    ) ?? false;

  const quizUrl = `${storeConfig.appUrl}${PATHS.MEMBER_DASHBOARD.WORKSHOP_MONEY_PERSONALITY_QUIZ(workshopId)}`;

  return (
    <div className="flex h-full flex-col">
      {hasResponses && data ? (
        <QuizResults results={data} showQuestion={false} />
      ) : (
        <NoResponsesMessage message="Responses are shown live when quizzes are completed." />
      )}
      <Divider className="border-gray-20! bg-gray-20 mb-3" />
      <div className="flex h-full flex-col">
        <QrCodePrompt
          activityUrl={quizUrl}
          slug="nerd-free-spirit-quiz"
          dialogTitle="Money Personality Quiz QR Code"
          linkLabel="Nerd/Free Spirit Quiz"
        >
          <Text className="text-5 text-blue-90 leading-lg font-bold">
            Scan the QR code to take the Nerd/Free Spirit quiz!
          </Text>
          <Text className="text-4 leading-lg mb-1">
            Take this fun quiz to figure out if you&apos;re more of a Nerd or a
            Free Spirit! If you&apos;re married, this will help you identify
            your role in the Budget Committee Meeting. If you&apos;re single,
            this will help you determine your strengths in creating your budget,
            and where you&apos;ll need some accountability.
          </Text>
        </QrCodePrompt>
      </div>
    </div>
  );
}

function TheRamseyShowClip() {
  return (
    <HLSVideoPlayer
      src="https://cdn.ramseysolutions.net/media/ramsey-plus/financial-peace/coordinator/dashboard/budget-workshop-assets/budgeting-basics.mp4"
      thumbnail="https://cdn.ramseysolutions.net/media/ramsey-plus/financial-peace/coordinator/dashboard/budget-workshop-assets/budgeting-basics.jpg"
    />
  );
}

const DISCUSSION_QUESTIONS: DiscussionQuestion[] = [
  {
    icon: 'https://cdn.ramseysolutions.net/media/ramsey-plus/financial-peace/coordinator/dashboard/budget-workshop-assets/discussion-icon-1.png',
    text: "What brought you here today? What's one thing you're hoping a budget helps you actually do or stop worrying about?"
  },
  {
    icon: 'https://cdn.ramseysolutions.net/media/ramsey-plus/financial-peace/coordinator/dashboard/budget-workshop-assets/discussion-icon-2.png',
    text: "What's your money personality? Did your nerd/free spirit results surprise you — or did they nail it?"
  },
  {
    icon: 'https://cdn.ramseysolutions.net/media/ramsey-plus/financial-peace/coordinator/dashboard/budget-workshop-assets/discussion-icon-3.png',
    text: "What's your biggest budget blocker? Is it tracking, sticking to it, or just getting started?"
  }
];

function Discussion() {
  return <DiscussionQuestions questions={DISCUSSION_QUESTIONS} />;
}

const actionItems: WorkshopActionItemDefinition[] = [
  { title: 'Getting Started', Content: GettingStarted },
  { title: 'Setting Up Your Account', Content: SettingUpYourAccount },
  { title: 'Meet Your Guides', Content: MeetYourGuides },
  { title: 'The 7 Baby Steps', Content: TheSevenBabySteps },
  {
    title: 'Zero-Based Budget',
    Content: ZeroBasedBudgetClip,
    icon: VideoOutlinedIcon,
    duration: '2:49'
  },
  { title: 'Nerd/Free Spirit Quiz', Content: NerdFreeSpiritQuiz },
  {
    title: 'Budgeting Basics',
    Content: TheRamseyShowClip,
    icon: VideoOutlinedIcon,
    duration: '3:13'
  },
  { title: 'Discussion', Content: Discussion }
];

export default actionItems;
